import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Link as LinkIcon, Mail, Lock, User, Chrome, ChevronDown, Search, Check,
  ArrowRight, ArrowLeft, GraduationCap, Briefcase, MapPin,
  Globe, Hash, Camera, X
} from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError, uploadAPI, invitesAPI } from '../lib/api';
import { Progress } from '../components/ui/progress';
import { HBCU_LIST } from '../lib/hbcuList';

const INDUSTRIES = [
  'Technology', 'Healthcare', 'Finance', 'Education', 'Marketing', 'Law',
  'Real Estate', 'Consulting', 'Government', 'Nonprofit', 'Media',
  'Engineering', 'Arts & Design', 'Science', 'Retail', 'Manufacturing', 'Other'
];

const INTERESTS = [
  'Digital Marketing', 'Travel', 'Photography', 'Mentorship', 'Fitness',
  'Cooking', 'Entrepreneurship', 'Real Estate', 'Finance', 'Technology',
  'Music', 'Art', 'Sports', 'Fashion', 'Writing', 'Public Speaking',
  'Volunteering', 'Investing', 'Reading', 'Gaming', 'Networking'
];

const BACKGROUND_IMAGES = [
  'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1600&q=80', // Woman and man at monitor
  'https://images.unsplash.com/photo-1521790361543-f645cf042ec4?w=1600&q=80', // Hands on macbook flat lay
  'https://images.unsplash.com/photo-1573167627769-e201a7ddf409?w=1600&q=80', // Women at conference table
  'https://images.unsplash.com/photo-1679117349740-c46c819d0373?w=1600&q=80', // Man in suit on phone
  'https://images.unsplash.com/photo-1622842547373-5aa4bf062dc5?w=1600&q=80', // Person in black long sleeve
  'https://images.unsplash.com/photo-1674052353268-6412e0781b4a?w=1600&q=80', // Men sitting on stool
  'https://images.unsplash.com/photo-1709811240710-cff5f04deb44?w=1600&q=80', // Woman in graduation cap
  'https://images.unsplash.com/photo-1764264829056-2775046bcf42?w=1600&q=80', // Woman in suit and sunglasses on rooftop
  'https://images.unsplash.com/photo-1765648684898-2213d6b11c5d?w=1600&q=80', // Woman at outdoor cafe with laptop
  'https://images.unsplash.com/photo-1765648496267-a0b528fb97fb?w=1600&q=80', // Woman typing on laptop at desk
  'https://images.unsplash.com/photo-1616804947838-6646ae0e423d?w=1600&q=80', // Man in blue suit beside black car
  'https://images.unsplash.com/photo-1739289696461-76363e419fae?w=1600&q=80', // Man sitting on chair reading
  'https://images.unsplash.com/photo-1552652893-2aa10a0ab4df?w=1600&q=80', // Man standing near high-rise building
  'https://images.unsplash.com/photo-1615572359976-1fe39507ed7b?w=1600&q=80', // Man in gray shirt near bridge
  'https://images.unsplash.com/photo-1611432579402-7037e3e2c1e4?w=1600&q=80', // Man in yellow blazer smiling
  'https://images.unsplash.com/photo-1589156280159-27698a70f29e?w=1600&q=80', // Woman in white collared shirt city night
  'https://images.unsplash.com/photo-1597711662967-b5dac822f5ac?w=1600&q=80', // March on Washington demonstrators
  'https://images.unsplash.com/photo-1597702693388-c985cfd3eaea?w=1600&q=80', // Reflecting pool March on Washington 1963
  'https://images.unsplash.com/photo-1648009847543-7491343aaec8?w=1600&q=80', // I am Black History t-shirt
  'https://images.unsplash.com/photo-1697183381312-0f2ac034bddc?w=1600&q=80', // Men with signs at US Capitol civil rights
];

const STEPS = [
  { title: 'Create Account', icon: User },
  { title: 'Your School', icon: GraduationCap },
  { title: 'Basic Info', icon: User },
  { title: 'Professional', icon: Briefcase },
  { title: 'Personal', icon: MapPin },
  { title: 'Links', icon: Globe },
  { title: 'Interests', icon: Hash },
];

const Register = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [institution, setInstitution] = useState('');
  const [institutionSearch, setInstitutionSearch] = useState('');
  const [showInstitutionDropdown, setShowInstitutionDropdown] = useState(false);
  const [name, setName] = useState('');
  const [graduationYear, setGraduationYear] = useState('');
  const [major, setMajor] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [company, setCompany] = useState('');
  const [industry, setIndustry] = useState('');
  const [hometown, setHometown] = useState('');
  const [currentLocation, setCurrentLocation] = useState('');
  const [greekLife, setGreekLife] = useState('');
  const [phone, setPhone] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [website, setWebsite] = useState('');
  const [bio, setBio] = useState('');
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [inviteToken, setInviteToken] = useState(null);
  const [inviteCanBypass, setInviteCanBypass] = useState(false);

  useEffect(() => {
    const prefillEmail = searchParams.get('email');
    const prefillName = searchParams.get('name');
    if (prefillEmail) setEmail(prefillEmail);
    if (prefillName) setName(prefillName);

    const token = searchParams.get('invite');
    if (token) {
      setInviteToken(token);
      invitesAPI.lookup(token)
        .then(res => {
          const data = res.data || {};
          if (data.email) setEmail(prev => prev || data.email);
          if (data.can_bypass_hbcu) setInviteCanBypass(true);
          if (data.expired) toast.error('This invite has expired. Please reach out for a new one.');
          else if (data.accepted) toast.error('This invite has already been used.');
        })
        .catch(() => toast.error('Could not validate invite link. You can still register normally.'));
    }
  }, [searchParams]);

  const [bgImage] = useState(BACKGROUND_IMAGES[Math.floor(Math.random() * BACKGROUND_IMAGES.length)]);

  const filteredInstitutions = HBCU_LIST.filter(inst =>
    inst.name.toLowerCase().includes(institutionSearch.toLowerCase()) ||
    inst.state.toLowerCase().includes(institutionSearch.toLowerCase())
  );

  const progressPercent = Math.round((step / STEPS.length) * 100);

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Photo must be under 10MB'); return; }
    setProfilePhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const toggleInterest = (interest) => {
    setSelectedInterests(prev =>
      prev.includes(interest) ? prev.filter(i => i !== interest)
        : prev.length < 10 ? [...prev, interest]
        : (toast.info('Maximum 10 interests'), prev)
    );
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register(email, password, name || email.split('@')[0]);
      toast.success('Account created!');
      setStep(2);
    } catch (error) {
      const code = error?.code || '';
      const msg = code === 'auth/email-already-in-use' ? 'This email is already registered. Try signing in instead.'
        : code === 'auth/network-request-failed' ? 'Network error — please check your internet connection, disable any ad blockers/VPN, and try again.'
        : code === 'auth/invalid-email' ? 'Please enter a valid email address.'
        : code === 'auth/weak-password' ? 'Password is too weak. Please use at least 6 characters.'
        : code === 'auth/operation-not-allowed' ? 'Email registration is temporarily unavailable. Try signing up with Google.'
        : code === 'auth/too-many-requests' ? 'Too many attempts. Please wait a moment and try again.'
        : error?.message || formatApiError(error);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const handleSaveProfile = async () => {
    if (!institution && !inviteCanBypass) { toast.error('Please select your institution'); setStep(2); return; }
    setLoading(true);
    try {
      let photoUrl = null;
      if (profilePhoto) {
        try { const r = await uploadAPI.profilePhoto(profilePhoto); photoUrl = r.data.url; } catch {}
      }
      const profileData = {};
      if (name) profileData.name = name;
      if (institution) profileData.institution = institution;
      if (graduationYear) profileData.graduation_year = parseInt(graduationYear);
      if (major) profileData.major = major;
      if (title) profileData.title = title;
      if (company) profileData.company = company;
      if (industry) profileData.industry = industry;
      if (hometown) profileData.hometown = hometown;
      if (currentLocation) profileData.current_location = currentLocation;
      if (greekLife) profileData.greek_life = greekLife;
      if (phone) profileData.phone = phone;
      if (linkedinUrl) profileData.linkedin_url = linkedinUrl;
      if (website) profileData.website = website;
      if (bio) profileData.bio = bio;
      if (selectedInterests.length > 0) profileData.interests = selectedInterests;
      if (photoUrl) profileData.profile_photo = photoUrl;
      const updated = await updateProfile(profileData);
      if (inviteToken) {
        try { await invitesAPI.accept(inviteToken, updated?.user_id); } catch {}
      }
      toast.success('Welcome to GradLink!');
      navigate('/feed');
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  };

  const handleNext = () => { if (step < STEPS.length) setStep(step + 1); };
  const handleBack = () => { if (step > 2) setStep(step - 1); };
  const canSkipToEnd = step >= 3;

  return (
    <div className="min-h-screen flex">
      {/* Left: Background Image (desktop) */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bgImage})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-dark-slate via-dark-slate/85 to-dark-slate/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-dark-slate via-dark-slate/40 to-dark-slate/20" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-white/15 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/20">
              <LinkIcon className="w-8 h-8 text-white" />
            </div>
            <span className="font-heading font-extrabold text-3xl text-white [text-shadow:_0_2px_8px_rgba(0,0,0,0.5)]">GradLink</span>
          </div>
          <div className="max-w-lg bg-dark-slate/60 backdrop-blur-md rounded-3xl p-8 border border-white/10 shadow-2xl">
            <h2 className="font-heading font-extrabold text-4xl text-white leading-tight mb-4 [text-shadow:_0_2px_12px_rgba(0,0,0,0.7)]">
              Join Your Alumni Community
            </h2>
            <p className="text-white/90 text-lg [text-shadow:_0_1px_6px_rgba(0,0,0,0.6)]">
              Build your profile, connect with mentors, find opportunities, and grow your network.
            </p>
          </div>
          <p className="text-white/50 text-xs">Photos from Unsplash</p>
        </div>
      </div>

      {/* Right: Registration Form */}
      <div className="flex-1 lg:max-w-xl flex flex-col justify-center p-6 bg-white relative overflow-y-auto">
        {/* Mobile background */}
        <div className="absolute inset-0 lg:hidden overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${bgImage})` }} />
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/70 to-white/90" />
        </div>

        <div className="relative w-full max-w-md mx-auto z-10">
          {/* Mobile logo */}
          <div className="text-center mb-6 lg:hidden">
            <div className="inline-flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-dark-slate rounded-2xl flex items-center justify-center shadow-lg">
                <LinkIcon className="w-6 h-6 text-mint-fresh" />
              </div>
              <h1 className="font-heading font-extrabold text-2xl text-dark-slate">GradLink</h1>
            </div>
          </div>

          {/* Progress (steps 2+) */}
          {step >= 2 && (
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-gray font-medium">Step {step} of {STEPS.length}</span>
                <span className="text-xs text-slate-gray">{progressPercent}%</span>
              </div>
              <Progress value={progressPercent} className="h-1.5" />
              <div className="flex justify-between mt-3">
                {STEPS.map((s, i) => (
                  <button key={i} onClick={() => i + 1 <= step && i + 1 >= 2 && setStep(i + 1)}
                    className={i + 1 <= step ? 'cursor-pointer' : 'cursor-default'} disabled={i + 1 > step || i + 1 < 2}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      i + 1 === step ? 'bg-dark-slate text-white' : i + 1 < step ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-gray'
                    }`}>{i + 1 < step ? <Check className="w-3.5 h-3.5" /> : i + 1}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Create Account */}
          {step === 1 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 lg:shadow-none lg:border-0 lg:p-0">
              <h2 className="font-heading font-bold text-2xl text-dark-slate text-center mb-6">Create Your Account</h2>
              <form onSubmit={handleCreateAccount} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-dark-slate font-medium text-sm">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
                    <Input type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12 rounded-xl bg-slate-50 border-slate-200" required data-testid="register-email" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-dark-slate font-medium text-sm">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
                    <Input type="password" placeholder="At least 6 characters" value={password} onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 h-12 rounded-xl bg-slate-50 border-slate-200" required minLength={6} data-testid="register-password" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-dark-slate font-medium text-sm">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
                    <Input type="password" placeholder="Confirm password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 h-12 rounded-xl bg-slate-50 border-slate-200" required data-testid="register-confirm" />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 bg-dark-slate text-white rounded-full font-semibold" data-testid="register-submit">
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-slate-gray">or</span></div>
              </div>
              <Button type="button" variant="outline" onClick={async () => {
                try { const r = await loginWithGoogle(); if (r) { toast.success('Welcome!'); navigate('/feed'); } }
                catch (e) { if (e?.code !== 'auth/popup-closed-by-user') toast.error(e?.message || 'Sign-in failed'); }
              }} className="w-full h-12 rounded-full border-2 border-slate-200 hover:border-dark-slate" data-testid="google-register">
                <Chrome className="w-5 h-5 mr-2" />Sign up with Google
              </Button>
              <p className="text-center text-slate-gray mt-5 text-sm">
                Already have an account? <Link to="/login" className="text-dark-slate font-semibold hover:underline">Sign In</Link>
              </p>
              <p className="text-center text-slate-gray mt-3 text-sm">
                Looking to recruit, advertise, or manage your alumni association?{' '}
                <Link to="/role-signup" className="text-dark-slate font-semibold hover:underline">Sign up as a partner &rarr;</Link>
              </p>
            </div>
          )}

          {/* Step 2: Institution (REQUIRED) */}
          {step === 2 && (
            <>
              <h2 className="font-heading font-bold text-xl text-dark-slate text-center mb-2">Select Your Institution</h2>
              <p className="text-slate-gray text-center text-sm mb-5">This is required to connect you with your alumni community.</p>
              <div className="relative mb-4">
                <button type="button" onClick={() => setShowInstitutionDropdown(!showInstitutionDropdown)}
                  className={`w-full h-12 px-4 rounded-xl border flex items-center justify-between text-left transition-colors ${
                    institution ? 'bg-mint-fresh/10 border-emerald-300' : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                  }`}>
                  <span className={institution ? 'text-dark-slate font-medium' : 'text-slate-gray'}>{institution || 'Choose your HBCU...'}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-gray transition-transform ${showInstitutionDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showInstitutionDropdown && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowInstitutionDropdown(false)} />
                    <div className="absolute z-50 w-full mt-2 bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden">
                      <div className="p-3 border-b border-slate-100">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                          <input type="text" placeholder="Search HBCUs..." value={institutionSearch}
                            onChange={(e) => setInstitutionSearch(e.target.value)}
                            className="w-full h-10 pl-9 pr-4 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:border-mint-fresh" autoFocus />
                        </div>
                      </div>
                      <div className="max-h-72 overflow-y-auto overscroll-contain">
                        {filteredInstitutions.map(inst => (
                          <button key={inst.id} onClick={() => { setInstitution(inst.name); setShowInstitutionDropdown(false); setInstitutionSearch(''); }}
                            className="w-full px-4 py-2.5 text-left text-sm hover:bg-mint-fresh/20 flex items-center justify-between">
                            <div><span className="text-dark-slate font-medium">{inst.name}</span><span className="text-slate-gray ml-2 text-xs">({inst.state})</span></div>
                            {institution === inst.name && <Check className="w-4 h-4 text-emerald-600" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
              {institution && <p className="text-emerald-600 text-xs font-medium mb-4">GradLink for {institution}</p>}
              <Button onClick={handleNext} disabled={!institution && !inviteCanBypass} className="w-full h-12 bg-dark-slate text-white rounded-full font-semibold disabled:opacity-50">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              {inviteCanBypass ? (
                <>
                  <button
                    type="button"
                    onClick={() => { setInstitution(''); handleNext(); }}
                    className="w-full mt-3 text-center text-sm text-dark-slate font-medium hover:underline"
                    data-testid="skip-hbcu"
                  >
                    I'm not an HBCU alum — continue without selecting a school
                  </button>
                  <p className="text-center text-xs text-emerald-600 mt-2">You were invited and can sign up without an HBCU.</p>
                </>
              ) : (
                <p className="text-center text-xs text-slate-gray mt-3">Institution selection is required to connect with your alumni community.</p>
              )}
            </>
          )}

          {/* Step 3: Basic Info */}
          {step === 3 && (
            <>
              <h2 className="font-heading font-bold text-xl text-dark-slate text-center mb-5">Basic Information</h2>
              <div className="space-y-4">
                <div className="flex justify-center mb-2">
                  <label className="relative cursor-pointer group">
                    <div className="w-24 h-24 rounded-2xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden group-hover:border-mint-fresh">
                      {photoPreview ? <img src={photoPreview} alt="" className="w-full h-full object-cover" /> : <Camera className="w-8 h-8 text-slate-gray" />}
                    </div>
                    {photoPreview && <button onClick={(e) => { e.preventDefault(); setProfilePhoto(null); setPhotoPreview(null); }}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-dark-slate text-white rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>}
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoSelect} />
                    <p className="text-xs text-slate-gray text-center mt-1">Add photo</p>
                  </label>
                </div>
                <div><Label className="text-sm">Full Name</Label><Input placeholder="Your full name" value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-xl bg-slate-50 mt-1" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-sm">Graduation Year</Label><Input type="number" placeholder="2020" value={graduationYear} onChange={(e) => setGraduationYear(e.target.value)} className="h-11 rounded-xl bg-slate-50 mt-1" /></div>
                  <div><Label className="text-sm">Major</Label><Input placeholder="Computer Science" value={major} onChange={(e) => setMajor(e.target.value)} className="h-11 rounded-xl bg-slate-50 mt-1" /></div>
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <Button variant="outline" onClick={handleBack} className="h-11 rounded-full px-5"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button onClick={handleNext} className="flex-1 h-11 bg-dark-slate text-white rounded-full">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
              {canSkipToEnd && <Button variant="link" onClick={handleSaveProfile} className="w-full mt-2 text-slate-gray text-sm">Skip to finish</Button>}
            </>
          )}

          {/* Step 4: Professional */}
          {step === 4 && (
            <>
              <h2 className="font-heading font-bold text-xl text-dark-slate text-center mb-5">Professional Info</h2>
              <div className="space-y-4">
                <div><Label className="text-sm">Current Title</Label><Input placeholder="Software Engineer" value={title} onChange={(e) => setTitle(e.target.value)} className="h-11 rounded-xl bg-slate-50 mt-1" /></div>
                <div><Label className="text-sm">Company</Label><Input placeholder="Google" value={company} onChange={(e) => setCompany(e.target.value)} className="h-11 rounded-xl bg-slate-50 mt-1" /></div>
                <div><Label className="text-sm">Industry</Label>
                  <Select value={industry} onValueChange={setIndustry}><SelectTrigger className="h-11 rounded-xl bg-slate-50 mt-1"><SelectValue placeholder="Select industry" /></SelectTrigger>
                    <SelectContent>{INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div className="flex space-x-3 mt-6">
                <Button variant="outline" onClick={handleBack} className="h-11 rounded-full px-5"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button onClick={handleNext} className="flex-1 h-11 bg-dark-slate text-white rounded-full">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
              <Button variant="link" onClick={handleSaveProfile} className="w-full mt-2 text-slate-gray text-sm">Skip to finish</Button>
            </>
          )}

          {/* Step 5: Personal */}
          {step === 5 && (
            <>
              <h2 className="font-heading font-bold text-xl text-dark-slate text-center mb-5">Personal Details</h2>
              <div className="space-y-4">
                <div><Label className="text-sm">Hometown</Label><Input placeholder="Atlanta, GA" value={hometown} onChange={(e) => setHometown(e.target.value)} className="h-11 rounded-xl bg-slate-50 mt-1" /></div>
                <div><Label className="text-sm">Current Location</Label><Input placeholder="New York, NY" value={currentLocation} onChange={(e) => setCurrentLocation(e.target.value)} className="h-11 rounded-xl bg-slate-50 mt-1" /></div>
                <div><Label className="text-sm">Greek Life <span className="text-slate-gray">(optional)</span></Label><Input placeholder="Alpha Kappa Alpha Sorority, Inc." value={greekLife} onChange={(e) => setGreekLife(e.target.value)} className="h-11 rounded-xl bg-slate-50 mt-1" /></div>
                <div><Label className="text-sm">Phone <span className="text-slate-gray">(optional)</span></Label><Input type="tel" placeholder="(555) 123-4567" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-11 rounded-xl bg-slate-50 mt-1" /></div>
              </div>
              <div className="flex space-x-3 mt-6">
                <Button variant="outline" onClick={handleBack} className="h-11 rounded-full px-5"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button onClick={handleNext} className="flex-1 h-11 bg-dark-slate text-white rounded-full">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
              <Button variant="link" onClick={handleSaveProfile} className="w-full mt-2 text-slate-gray text-sm">Skip to finish</Button>
            </>
          )}

          {/* Step 6: Links */}
          {step === 6 && (
            <>
              <h2 className="font-heading font-bold text-xl text-dark-slate text-center mb-5">Social Links</h2>
              <div className="space-y-4">
                <div><Label className="text-sm">LinkedIn URL</Label><Input type="url" placeholder="https://linkedin.com/in/yourname" value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} className="h-11 rounded-xl bg-slate-50 mt-1" /></div>
                <div><Label className="text-sm">Personal Website</Label><Input type="url" placeholder="https://yourwebsite.com" value={website} onChange={(e) => setWebsite(e.target.value)} className="h-11 rounded-xl bg-slate-50 mt-1" /></div>
              </div>
              <div className="flex space-x-3 mt-6">
                <Button variant="outline" onClick={handleBack} className="h-11 rounded-full px-5"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button onClick={handleNext} className="flex-1 h-11 bg-dark-slate text-white rounded-full">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
              <Button variant="link" onClick={handleSaveProfile} className="w-full mt-2 text-slate-gray text-sm">Skip to finish</Button>
            </>
          )}

          {/* Step 7: Interests */}
          {step === 7 && (
            <>
              <h2 className="font-heading font-bold text-xl text-dark-slate text-center mb-5">Interests & Bio</h2>
              <div className="space-y-4">
                <div><Label className="text-sm">About You <span className="text-slate-gray">({bio.length}/500)</span></Label>
                  <Textarea placeholder="Tell the community about yourself..." value={bio} maxLength={500} onChange={(e) => setBio(e.target.value)} className="rounded-xl bg-slate-50 mt-1 resize-none" rows={3} /></div>
                <div><Label className="text-sm">Select Interests <span className="text-slate-gray">({selectedInterests.length}/10)</span></Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {INTERESTS.map(interest => (
                      <button key={interest} onClick={() => toggleInterest(interest)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          selectedInterests.includes(interest) ? 'bg-dark-slate text-white' : 'bg-slate-100 text-slate-gray hover:bg-slate-200'
                        }`}>{interest}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <Button variant="outline" onClick={handleBack} className="h-11 rounded-full px-5"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button onClick={handleSaveProfile} disabled={loading} className="flex-1 h-12 bg-dark-slate text-white rounded-full font-semibold">
                  {loading ? 'Finishing up...' : 'Enter GradLink'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
